import { UzButton, UzTabs } from '@utazzo/ui';
import { ArrowLeft, Mail, User as UserIcon } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router-dom';

import { UserEmailLogPanel } from '../components/UserEmailLogPanel';
import { UserProfilePanel } from '../components/UserProfilePanel';
import { useUserDetail } from '../use-users';

import { PageShell } from '@/components/page-shell';
import { useListReturn } from '@/hooks/use-list-return';

/**
 * User detail screen (ADR-0042 follow-up). Hosts two tabs:
 *   - Profile: read-only summary + the existing form drawer for edit.
 *   - Email log: every welcome / reset attempt for this user, status
 *     + last error + retry affordance.
 *
 * Reachable from the Users list "View details" action. Server-gated
 * by the same admin role as the rest of the Users module.
 */
export const UserDetailPage = (): React.JSX.Element => {
  const backToList = useListReturn('/users');
  const params = useParams<{ id: string }>();
  const id = params.id ?? null;
  const user = useUserDetail(id);

  if (user.isLoading) {
    return (
      <PageShell title="User" description="">
        <div className="p-6 text-sm text-zinc-500">Loading user...</div>
      </PageShell>
    );
  }
  if (user.error || !user.data) {
    return (
      <PageShell title="User" description="">
        <div className="p-6 text-sm text-red-600">Could not load this user.</div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={user.data.fullName}
      description={user.data.email}
      actions={
        <UzButton variant="ghost" onClick={backToList}>
          <ArrowLeft className="mr-1 size-4" /> Back to Users
        </UzButton>
      }
    >
      <UzTabs
        defaultValue="profile"
        tabs={[
          {
            value: 'profile',
            label: (
              <span className="inline-flex items-center gap-1.5">
                <UserIcon className="size-4" /> Profile
              </span>
            ),
            content: <UserProfilePanel user={user.data} />,
          },
          {
            value: 'email-log',
            label: (
              <span className="inline-flex items-center gap-1.5">
                <Mail className="size-4" /> Email log
              </span>
            ),
            content: <UserEmailLogPanel userId={user.data.id} />,
          },
        ]}
      />
    </PageShell>
  );
};
